import { format } from "date-fns";
import { useLayoutEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import ReactDOM from "react-dom";
import MaterialIcon from "../material-icon/material-icon";
import styles from "./app-date-picker.module.scss";

/** Gap between the trigger and the calendar, and the minimum breathing room
 * we keep against the viewport edges. */
const GAP = 8;
const VIEWPORT_MARGIN = 8;

interface IProps {
  selectedDate: Date;
  dateFormat?: string;
  inputDateFormat?: string;
  onChange: (date: Date) => void;
  monthYearPicker?: boolean;
  yearPicker?: boolean;
  minDate?: Date;
  maxDate?: Date;
  isInput?: boolean;
  fontSize?: number;
  label?: string;
  showDate?: boolean;
  error?: string;
  disabled?: boolean;
}

const AppDatePicker = (props: IProps) => {
  const {
    dateFormat = "yyyy MMMM",
    inputDateFormat = "E(dd), MMM, yyyy",
    selectedDate,
    monthYearPicker,
    yearPicker,
    minDate,
    maxDate,
    onChange,
    isInput,
    fontSize,
    label,
    showDate = true,
    error,
    disabled,
  } = props;
  const [isOpen, setIsOpen] = useState(false);

  const anchorRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );

  /** The calendar is portalled to <body> and positioned against the trigger so
   * that it is never clipped by a scrollable ancestor (e.g. the modal card,
   * which needs overflow: auto for tall content).
   *
   * Vertical placement prefers, in order: below the trigger within the
   * enclosing popover boundary (the modal card, if any), above it within that
   * boundary, then below/above within the viewport, and finally clamped to
   * the viewport. */
  useLayoutEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }

    const reposition = () => {
      const anchor = anchorRef.current;
      const popover = popoverRef.current;
      if (!anchor || !popover) return;

      const rect = anchor.getBoundingClientRect();
      const width = popover.offsetWidth;
      const height = popover.offsetHeight;

      const viewportTop = VIEWPORT_MARGIN;
      const viewportBottom = window.innerHeight - VIEWPORT_MARGIN;

      const boundaryRect = anchor
        .closest("[data-popover-boundary]")
        ?.getBoundingClientRect();
      const boundaryTop = boundaryRect
        ? Math.max(viewportTop, boundaryRect.top + VIEWPORT_MARGIN)
        : viewportTop;
      const boundaryBottom = boundaryRect
        ? Math.min(viewportBottom, boundaryRect.bottom - VIEWPORT_MARGIN)
        : viewportBottom;

      const below = rect.bottom + GAP;
      const above = rect.top - GAP - height;

      let top: number;
      if (below + height <= boundaryBottom) {
        top = below;
      } else if (above >= boundaryTop) {
        top = above;
      } else if (boundaryRect && boundaryBottom - boundaryTop >= height) {
        // Fits in the boundary, but not cleanly on either side of the trigger:
        // stay inside it on the roomier side, overlapping the trigger a little
        // rather than spilling out of the modal.
        top =
          boundaryBottom - rect.bottom >= rect.top - boundaryTop
            ? boundaryBottom - height
            : boundaryTop;
      } else if (below + height <= viewportBottom) {
        top = below;
      } else if (above >= viewportTop) {
        top = above;
      } else {
        top = Math.max(viewportTop, viewportBottom - height);
      }

      const maxLeft = Math.max(
        VIEWPORT_MARGIN,
        window.innerWidth - VIEWPORT_MARGIN - width,
      );
      let left = rect.right - width;
      if (left < VIEWPORT_MARGIN) left = rect.left;
      left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));

      setCoords({ top, left });
    };

    reposition();
    window.addEventListener("resize", reposition);
    // capture: true so scrolling in any ancestor container repositions it too
    window.addEventListener("scroll", reposition, true);
    // the calendar's height changes with the number of week rows per month
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(reposition)
        : null;
    if (popoverRef.current) resizeObserver?.observe(popoverRef.current);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      resizeObserver?.disconnect();
    };
  }, [isOpen]);

  const handleChange = (date: Date) => {
    setIsOpen(!isOpen);
    onChange(date);
  };

  const previousMonth = () => {
    if (yearPicker) {
      onChange(
        new Date(selectedDate.setFullYear(selectedDate.getFullYear() - 1)),
      );
      return;
    } else if (monthYearPicker) {
      onChange(new Date(selectedDate.setMonth(selectedDate.getMonth() - 1)));
      return;
    }
    onChange(new Date(selectedDate.setDate(selectedDate.getDate() - 1)));
  };

  const nextMonth = () => {
    if (yearPicker) {
      onChange(
        new Date(selectedDate.setFullYear(selectedDate.getFullYear() + 1)),
      );
      return;
    } else if (monthYearPicker) {
      onChange(new Date(selectedDate.setMonth(selectedDate.getMonth() + 1)));
      return;
    }
    onChange(new Date(selectedDate.setDate(selectedDate.getDate() + 1)));
  };

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {label && <label className="font-size-14">{label}</label>}
      <div
        ref={anchorRef}
        className={`month-selection ${
          showDate
            ? `month-selection-min-width-120`
            : `month-selection-min-width-52`
        } ${disabled && `month-selection-disabled`}`}
      >
        <Row className="justify-content-between align-items-center flex-nowrap w-100">
          {isInput ? (
            <>
              {showDate && (
                <Col className="col-auto font-size-14">
                  <div
                    style={{ fontSize: `${fontSize ? fontSize + "px" : ""}` }}
                  >
                    {format(selectedDate, inputDateFormat)}
                  </div>
                </Col>
              )}
              <Col className="col-auto d-flex justify-content-center px-2">
                <MaterialIcon
                  onClick={!disabled ? handleClick : undefined}
                  color="#7483cc"
                  icon="calendar_month"
                  className={`${
                    disabled ? `cursor-disabled` : `cursor-pointer`
                  }`}
                />
              </Col>
            </>
          ) : (
            <>
              {minDate && selectedDate.getMonth() === minDate.getMonth() ? (
                <></>
              ) : (
                <Col className="col-auto px-1">
                  <MaterialIcon
                    icon="chevron_left"
                    className="text-light"
                    style={{ cursor: "pointer" }}
                    onClick={previousMonth}
                  />
                </Col>
              )}
              <Col className={`text-center cursor-pointer font-size-10`}>
                <div
                  style={{ fontSize: `${fontSize ? fontSize + "px" : ""}` }}
                  onClick={handleClick}
                >
                  {format(selectedDate, dateFormat)}
                </div>
              </Col>
              {maxDate && selectedDate.getMonth() === maxDate.getMonth() ? (
                <></>
              ) : (
                <Col className="col-auto px-1">
                  <MaterialIcon
                    icon="chevron_right"
                    className="text-light"
                    style={{ cursor: "pointer" }}
                    onClick={nextMonth}
                  />
                </Col>
              )}
            </>
          )}
        </Row>
      </div>
      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={popoverRef}
            className={styles.datePickerPopover}
            style={{
              top: coords?.top ?? 0,
              left: coords?.left ?? 0,
              // avoid a flash at 0,0 on the measuring pass
              visibility: coords ? "visible" : "hidden",
            }}
          >
            <DatePicker
              selected={selectedDate}
              onChange={handleChange}
              inline
              onClickOutside={handleClick}
              showMonthYearPicker={monthYearPicker}
              showYearPicker={yearPicker}
              calendarStartDay={1}
              // always render 6 week rows so the height (and therefore the
              // placement) doesn't jump while navigating between months
              fixedHeight
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>,
          document.body,
        )}
      {error && <div className="error mt-2">{error}</div>}
    </>
  );
};

export default AppDatePicker;
